import { Timestamp } from "firebase/firestore";

/**
 * Formata uma data para o padrão brasileiro 'dd/MM/yyyy'.
 * Esta função é versátil, aceitando tanto objetos `Date` nativos do JavaScript
 * quanto objetos `Timestamp` do Firebase Firestore, garantindo uma apresentação
 * uniforme e amigável ao usuário em toda a aplicação.
 *
 * Em termos de UX/UI, uma formatação de data consistente e legível é crucial
 * para a clareza das informações, evitando confusão e melhorando a experiência
 * geral do usuário ao interagir com dados temporais.
 *
 * @param data A data a ser formatada. Pode ser um objeto `Date` ou um `Timestamp` do Firestore.
 * @returns Uma string representando a data no formato 'dd/MM/yyyy'. Retorna uma string vazia
 *          se a entrada for nula, indefinida ou representar uma data inválida.
 */
export function formatarData(data: Date | Timestamp | null | undefined): string {
  let objetoData: Date;

  if (!data) {
    return '';
  }

  if (data instanceof Timestamp) {
    objetoData = data.toDate();
  } else if (data instanceof Date) {
    objetoData = data;
  } else {
    // Caso a entrada não seja um tipo esperado, tenta converter para Date
    // Isso pode ocorrer se for uma string de data, por exemplo.
    objetoData = new Date(data as any);
  }

  // Verifica se o objeto Date resultante é válido.
  // Uma data inválida (ex: new Date('abc')) retorna NaN para getTime().
  if (isNaN(objetoData.getTime())) {
    return '';
  }

  // Utiliza toLocaleDateString com o locale 'pt-BR' para garantir o formato desejado.
  // As opções '2-digit' asseguram que dia e mês tenham sempre dois dígitos, preenchendo com zero à esquerda se necessário.
  return objetoData.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Retorna a data e hora atual como um objeto `Date`.
 * Esta função utilitária oferece uma maneira simples e direta de obter
 * o momento exato da execução, útil para registros de `criado_em` e `atualizado_em`
 * ou para qualquer operação que exija a data atual.
 *
 * A simplicidade e clareza desta função contribuem para a manutenibilidade
 * e a previsibilidade do código, aspectos importantes para um design de software moderno.
 *
 * @returns Um objeto `Date` representando a data e hora atual no fuso horário local.
 */
export function obterDataAtual(): Date {
  return new Date();
}

/**
 * Converte um objeto `Date` para um `Timestamp` do Firebase Firestore.
 * Essencial para persistir datas no banco de dados NoSQL de forma padronizada.
 *
 * @param data O objeto `Date` a ser convertido.
 * @returns Um objeto `Timestamp` do Firebase.
 */
export function converterParaTimestamp(data: Date): Timestamp {
  return Timestamp.fromDate(data);
}

/**
 * Formata uma data e hora para o padrão brasileiro 'dd/MM/yyyy HH:mm'.
 * Ideal para exibir registros de transações ou eventos com precisão de minutos.
 *
 * @param data A data a ser formatada, pode ser um objeto Date ou um Timestamp do Firebase.
 * @returns Uma string representando a data e hora no formato 'dd/MM/yyyy HH:mm', ou uma string vazia se a data for inválida.
 */
export function formatarDataHora(data: Date | Timestamp | null | undefined): string {
  let objetoData: Date;

  if (!data) {
    return '';
  }

  if (data instanceof Timestamp) {
    objetoData = data.toDate();
  } else if (data instanceof Date) {
    objetoData = data;
  } else {
    objetoData = new Date(data as any);
  }

  if (isNaN(objetoData.getTime())) {
    return '';
  }

  const dataFormatada = objetoData.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  const horaFormatada = objetoData.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false, // Formato 24 horas
  });

  return `${dataFormatada} ${horaFormatada}`;
}

/**
 * Calcula a diferença em dias entre duas datas.
 * Útil para funcionalidades como validade de campanhas ou tempo de inatividade de clientes.
 *
 * @param dataInicio A data de início (Date ou Timestamp).
 * @param dataFim A data de fim (Date ou Timestamp).
 * @returns O número de dias entre as duas datas. Retorna 0 se as datas forem inválidas.
 */
export function calcularDiferencaEmDias(dataInicio: Date | Timestamp | null | undefined, dataFim: Date | Timestamp | null | undefined): number {
  const inicio = formatarData(dataInicio);
  const fim = formatarData(dataFim);

  if (!inicio || !fim) {
    return 0;
  }

  const [diaInicio, mesInicio, anoInicio] = inicio.split('/').map(Number);
  const [diaFim, mesFim, anoFim] = fim.split('/').map(Number);

  const date1 = new Date(anoInicio, mesInicio - 1, diaInicio);
  const date2 = new Date(anoFim, mesFim - 1, diaFim);

  if (isNaN(date1.getTime()) || isNaN(date2.getTime())) {
    return 0;
  }

  const diferencaTempo = Math.abs(date2.getTime() - date1.getTime());
  const diferencaDias = Math.ceil(diferencaTempo / (1000 * 60 * 60 * 24));

  return diferencaDias;
}